import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import { StatusBadge, PriorityBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import { listAdminTickets, adminTicketStats, deleteAdminTicket } from "../../api/adminTicketService";
import { listEmployees } from "../../api/employeeService";
import { listAllBusinesses } from "../../api/businessService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { fromNow, ticketNumber, initials, serialNumber } from "../../utils/formatters";
import { fetchAndDownload } from "../../utils/exportCsv";
import { showApiError, showToast } from "../../utils/toast";

const STATUS_TABS = [
  { id: "all", label: "All", params: {} },
  { id: "open", label: "Open", params: { status: "open" } },
  { id: "in_progress", label: "In progress", params: { status: "in_progress" } },
  { id: "resolved", label: "Resolved", params: { status: "resolved" } },
  { id: "closed", label: "Closed", params: { status: "closed" } },
  { id: "unassigned", label: "Unassigned", params: { unassigned: true } },
];

const EMPTY_FILTERS = {
  search: "",
  priority: "",
  customer_id: "",
  assigned_to: "",
  created_from: "",
  created_to: "",
};

export default function TicketsPage() {
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();
  const [tab, setTab] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 250);
    return () => clearTimeout(t);
  }, [filters.search]);

  // Reset page on any filter change.
  useEffect(() => {
    setPage(1);
  }, [tab, filters.priority, filters.customer_id, filters.assigned_to, filters.created_from, filters.created_to, debouncedSearch, perPage]);

  const tickets = useApi(
    () => {
      const tabParams = STATUS_TABS.find((f) => f.id === tab)?.params || {};
      const params = { ...tabParams, page, per_page: perPage };
      if (filters.priority) params.priority = filters.priority;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;
      if (debouncedSearch) params.search = debouncedSearch;
      return listAdminTickets(params);
    },
    [tab, page, perPage, filters.priority, filters.customer_id, filters.assigned_to, filters.created_from, filters.created_to, debouncedSearch, refreshKey],
  );
  const stats = useApi(() => adminTicketStats(), [refreshKey]);
  const employees = useApi(() => listEmployees({ per_page: 200 }));
  const clients = useApi(() => listAllBusinesses({ per_page: 500 }));

  const rows = paginatedRows(tickets.data);
  const meta = paginatedMeta(tickets.data);
  const employeeRows = paginatedRows(employees.data);
  const clientRows = paginatedRows(clients.data);

  const s = stats.data?.data;
  const counts = {
    all: s?.total ?? 0,
    open: s?.by_status?.open ?? 0,
    in_progress: s?.by_status?.in_progress ?? 0,
    resolved: s?.by_status?.resolved ?? 0,
    closed: s?.by_status?.closed ?? 0,
    unassigned: s?.unassigned ?? 0,
  };

  // Both filters key on the linked *user* id, not the business/employee id —
  // that's what the tickets table stores.
  const customerOptions = useMemo(
    () =>
      clientRows
        .filter((c) => c.user_id)
        .map((c) => ({
          id: c.user_id,
          label: `${c.client_id ? `${c.client_id} · ` : ""}${c.business_name}`,
        })),
    [clientRows],
  );
  const assigneeOptions = useMemo(
    () =>
      employeeRows
        .filter((e) => e.user_id)
        .map((e) => ({ id: e.user_id, label: e.name, hint: e.department?.name })),
    [employeeRows],
  );

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeFilterCount =
    Object.entries(filters).filter(([k, v]) => k !== "search" && v !== "" && v != null).length
    + (debouncedSearch ? 1 : 0);
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const exportTickets = async () => {
    setExporting(true);
    try {
      const tabParams = STATUS_TABS.find((f) => f.id === tab)?.params || {};
      const params = { ...tabParams };
      if (filters.priority) params.priority = filters.priority;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;
      if (debouncedSearch) params.search = debouncedSearch;
      await fetchAndDownload("/tickets/export", params, "tickets");
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Tickets</h1>
          <div className="page-sub">{meta?.total ?? rows.length} tickets total</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportTickets} disabled={exporting}>
            <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {STATUS_TABS.map((f) => (
          <button key={f.id} className={`chip ${tab === f.id ? "active" : ""}`} onClick={() => setTab(f.id)}>
            {f.label} <span className="chip-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <div
        className="card"
        style={{ marginBottom: 12, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}
      >
        {/* Row 1: search (full width) + clear */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Icon
              name="search"
              size={14}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
            />
            <input
              className="input"
              style={{ paddingLeft: 34 }}
              placeholder="Search subject, description, or customer name…"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
            />
          </div>
          <button className="btn btn-ghost" onClick={clearFilters} disabled={activeFilterCount === 0}>
            <Icon name="x" size={13} /> Clear filters
            {activeFilterCount > 0 && <span className="chip-count" style={{ marginLeft: 4 }}>{activeFilterCount}</span>}
          </button>
        </div>

        {/* Row 2: filter dropdowns */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
          <FilterField label="Priority">
            <select className="select" value={filters.priority} onChange={(e) => setFilter("priority", e.target.value)}>
              <option value="">Any priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </FilterField>
          <FilterField label="Customer">
            <SearchableSelect
              value={filters.customer_id}
              onChange={(v) => setFilter("customer_id", v ?? "")}
              options={customerOptions}
              placeholder="All customers"
              searchPlaceholder="Search customers…"
              width="100%"
            />
          </FilterField>
          <FilterField label="Assignee">
            <SearchableSelect
              value={filters.assigned_to}
              onChange={(v) => setFilter("assigned_to", v ?? "")}
              options={assigneeOptions}
              placeholder="All assignees"
              searchPlaceholder="Search assignees…"
              width="100%"
            />
          </FilterField>
          <FilterField label="Created from">
            <input type="date" className="input" value={filters.created_from} onChange={(e) => setFilter("created_from", e.target.value)} />
          </FilterField>
          <FilterField label="Created to">
            <input type="date" className="input" value={filters.created_to} onChange={(e) => setFilter("created_to", e.target.value)} />
          </FilterField>
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {tickets.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="message-circle"
              title="No tickets match the current filters"
              hint={activeFilterCount > 0 ? "Try clearing some filters." : undefined}
              action={activeFilterCount > 0 ? (
                <button className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
              ) : null}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th style={{ width: 80 }}>Ticket</th>
                  <th>Subject</th>
                  <th>Customer</th>
                  <th>Assignee</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Last update</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => (
                  <tr key={t.id} className="clickable" onClick={() => navigate(`/admin/tickets/${t.id}`)}>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td className="t-mono t-muted">{ticketNumber(t.id)}</td>
                    <td><div className="t-primary">{t.subject}</div></td>
                    <td className="t-muted">{t.customer?.name}</td>
                    <td>
                      {t.assignee ? (
                        <div className="row gap-sm">
                          <div className="avatar avatar-sm">{initials(t.assignee.name)}</div>
                          <span className="t-muted text-xs">{t.assignee.name?.split(" ")[0]}</span>
                        </div>
                      ) : (
                        <span className="badge badge-warning">Unassigned</span>
                      )}
                    </td>
                    <td><PriorityBadge priority={t.priority} /></td>
                    <td><StatusBadge status={t.status} /></td>
                    <td className="t-muted">{fromNow(t.updated_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => navigate(`/admin/tickets/${t.id}`)}
                        >
                          <Icon name="eye" size={13} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          style={{ color: "var(--danger)" }}
                          onClick={async () => {
                            if (!window.confirm(`Delete ticket ${ticketNumber(t.id)}? This also removes all its messages.`)) return;
                            try {
                              await deleteAdminTicket(t.id);
                              showToast("success", "Ticket deleted");
                              tickets.reload();
                              stats.reload();
                            } catch (err) {
                              showApiError(err);
                            }
                          }}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={tickets.loading}
          />
        </div>
      </div>
    </>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
